// ─── Cluster B — Job-state ('Phase 10.5' class-shape retrofit) ───────────────
// Per docs/design/class-shape-retrofit-audit-260522.md §4 (rank 3 retrofit;
// "Phase 10.5 prep") and docs/design/app-js-split-plan-260522.md §3 Cluster B.
//
// HISTORICAL: Originally extracted from app.js as `job-state.mjs` (Phase 2,
// 2026-05-22) carrying 7 mutable module-level state variables + 6 exported
// functions. The class-shape retrofit audit ranked this file #3 by
// state-bearing-ness and surfaced it as a CLAUDE.md rule 11a candidate
// (≥2 mutable module-private vars shared across exported functions → class).
//
// This file is the .ts retrofit. The module's mutable state now lives on a
// `JobState` class; a single module-level singleton (`jobState`) is exported.
// Consumers import the singleton and call methods. Per CLAUDE.md "Update
// callsites; don't preserve legacy shims" (codified 2026-05-22) this file
// does NOT re-export the legacy named exports (`trackedJobs`, `pendingRetries`,
// `applyJobUpdate`, etc.) as delegating shims. Callsites in `upload.mjs`,
// `polling.mjs`, and `renderers.ts` were updated in the same PR.
//
// Surviving free exports (pure functions / read-only constants):
//   readEnvelope          — pure 5-state derived_changelog reader, no state
//   ACTIVE_STATUSES       — derived const set (computed once at module load
//                            from window.MAIN_JOB_ACTIVE_STATUSES)
//
// See: docs/design/app-js-split-plan-260522.md §3 Cluster B
//      docs/design/class-shape-retrofit-audit-260522.md §4
//      docs/dev/CODE-STYLE.md §1a (class-vs-module shape)
//      docs/dev/AI-FIRST-ENGINEERING.md §2.3 (no-legacy-shim corollary)
//      docs/web/jobs-report-three-state-envelope-260517.md (readEnvelope contract)
//      docs/ux/ux-architecture-260506.md §"Job lifecycle"
// ─── 5-state derived_changelog envelope READ contract ───────────────────────
// Per docs/web/jobs-report-three-state-envelope-260517.md §1.3 + §7
// ruling #3, every completed job's `job.report` either:
//   (a) carries a `derived_changelog` envelope with a stored status
//       (no_issues_found / remediated / partial / <future stored>), OR
//   (b) is a pre-260517 legacy chunked report with top-level
//       total_changes + categories but no envelope, OR
//   (c) is null/undefined, or an envelope missing `status` (silent-
//       empty row from the early 260517 window — NOT backfilled).
//
// WHY (architect §7 ruling #3): `legacy_no_data` is a READ-DERIVED
// state — it is NEVER stored. We do NOT backfill (b)/(c) rows: the
// deriver registry cannot be reconstructed, so fabricating a stored
// status would be wrong. This reader is the total function that maps
// every absent/NULL status to the explicit `legacy_no_data` state so
// no consumer has to NULL-infer. Architect §7 ruling #5: consumers MUST
// consult the envelope; the wrapper-only path does NOT hoist legacy
// fields. Legacy chunked rows still surface total_changes + categories
// at the top level for display, but their status is legacy_no_data.
const ENVELOPE_LEGACY_NO_DATA = 'legacy_no_data';
export function readEnvelope(job) {
    if (!job?.report) {
        return {
            kind: 'empty', status: ENVELOPE_LEGACY_NO_DATA,
            totalChanges: 0, categories: [], summary: null,
            partialCount: null,
        };
    }
    const r = job.report;
    const env = r['derived_changelog'];
    if (env && typeof env === 'object') {
        const envCats = env['categories'];
        const rCats = r['categories'];
        const cats = Array.isArray(envCats)
            ? envCats
            : (Array.isArray(rCats) ? rCats : []);
        const envTotal = env['total_changes'];
        const envPartial = env['partial_completion_count'];
        return {
            kind: 'envelope',
            // Absent/NULL stored status → read-derived legacy_no_data
            // (never stored; architect §7 ruling #3).
            status: env['status'] ?? ENVELOPE_LEGACY_NO_DATA,
            totalChanges: envTotal != null ? Number(envTotal) : 0,
            categories: cats,
            summary: env['summary'] ?? null,
            partialCount: envPartial != null ? Number(envPartial) : null,
        };
    }
    // Legacy pre-260517 chunked report — no envelope, top-level fields
    // only. No stored status → legacy_no_data (read-derived).
    const rCats = r['categories'];
    const cats = Array.isArray(rCats)
        ? rCats
        : [];
    const totalItems = r['total_items'];
    const totalChanges = totalItems != null ? Number(totalItems)
        : (r['total_changes'] != null ? Number(r['total_changes']) : 0);
    return {
        kind: 'legacy',
        status: ENVELOPE_LEGACY_NO_DATA,
        totalChanges: totalChanges,
        categories: cats,
        summary: null,
        partialCount: null,
    };
}
// ─── ACTIVE_STATUSES ─────────────────────────────────────────────────────────
// Subset of MAIN_JOB_ACTIVE_STATUSES that the polling loop tracks.
// Excludes `cancelling`: when the user clicks Cancel we optimistically
// remove the row from the UI immediately, so the poll loop never needs to
// re-check it. Derived here so the canonical vocabulary stays in
// main-job-status.js and a new active state is one edit away. Read-only
// const (computed once at module load) — not state-bearing, so stays as a
// free export per the rule 11a class-shape retrofit (audit 260522 §4).
// Globals: window.MAIN_JOB_ACTIVE_STATUSES, window.MainJobStatus.
const _adaWindow = (typeof window !== 'undefined' ? window : {});
export const ACTIVE_STATUSES = new Set(_adaWindow.MAIN_JOB_ACTIVE_STATUSES
    ? [..._adaWindow.MAIN_JOB_ACTIVE_STATUSES].filter((s) => s !== (_adaWindow.MainJobStatus?.CANCELLING))
    : []);
// ─── JobState class (CLAUDE.md rule #11a) ────────────────────────────────────
//
// Encapsulates the 7 mutable module-level state vars that previously lived as
// free `export const` Maps + Objects + Set. The class collapses them into
// instance fields and exposes intent-bearing methods (trackJob / removeJob /
// listJobs etc.) so consumers don't reach into raw collections.
//
// Per CLAUDE.md "Update callsites; don't preserve legacy shims" (260522),
// this module exports the singleton `jobState` instead of the old named
// surface. The three consumers — `upload.mjs`, `polling.mjs`, and
// `renderers.ts` — were updated to call methods in the same PR.
export class JobState {
    /** Tracked jobs — the single source of truth for all in-flight and
     *  completed jobs visible to this SPA session. */
    _trackedJobs = {};
    /** Files waiting for server capacity (429 back-off). Key: synthetic ID,
     *  value: {file, name, retryTimer}. */
    _pendingRetries = {};
    /** "Report a problem" dialogs that the user has opened on completed-job
     *  cards. Persisted across SSE-driven re-renders of the done-list
     *  (otherwise the dialog snapped shut every time another job ticked). */
    _openReportDialogs = {};
    /** Jobs cleared this browser session. Prevents SSE and loadUserJobHistory
     *  from re-injecting them. Stored in sessionStorage so it resets on tab
     *  close. */
    _clearedJobIds;
    // Per-job monotonic-progress caches keyed by job.id. Enforce monotonic
    // percent and stage-label even when SSE / polling delivers a stale
    // frame after a later one. Cleared when the job hits a terminal state
    // (completed / failed / cancelled).
    _jobMonotonicPct = new Map();
    _jobMonotonicLabel = new Map();
    _jobMonotonicLabelPct = new Map();
    /** DI-injected setMainJobStatus; avoids window read inside trackJob. */
    _setMainJobStatus;
    constructor(deps) {
        this._clearedJobIds = new Set(typeof sessionStorage !== 'undefined'
            ? JSON.parse(sessionStorage.getItem('clearedJobIds') ?? '[]') // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            : []);
        this._setMainJobStatus = deps?.setMainJobStatus;
    }
    // ─── Tracked-job accessors ───────────────────────────────────────────────
    /** Look up a tracked job by id. */
    getJob(id) {
        return this._trackedJobs[id];
    }
    /** Whether a tracked job with this id exists. */
    hasJob(id) {
        return Object.prototype.hasOwnProperty.call(this._trackedJobs, id);
    }
    /** All tracked job ids. */
    listJobIds() {
        return Object.keys(this._trackedJobs);
    }
    /** All tracked jobs as an array. */
    listJobs() {
        return Object.values(this._trackedJobs);
    }
    /** Drop a tracked job by id. */
    removeJob(id) {
        delete this._trackedJobs[id];
    }
    /** Drop every tracked job (cancel-all path). */
    clearAllJobs() {
        this._trackedJobs = {};
    }
    /** Whether any tracked job has the given active-status set membership. */
    hasAnyJobMatching(predicate) {
        return Object.values(this._trackedJobs).some(predicate);
    }
    // ─── trackedJobs SM mutator (was applyJobUpdate) ─────────────────────────
    // Apply a server payload (SSE event, polling response, upload reply) to
    // the tracked-job entry by routing the status field through
    // setMainJobStatus (web/static/main-job-status.js). The legal-transition
    // table + stale-event guard replaces the prior pattern of unconditional
    // whole-row replacement, which silently accepted backward transitions
    // and races between SSE+polling. Non-status fields are merged via
    // Object.assign so client-only state (size, slide_count) survives a
    // server payload that omits them.
    //
    // Returns the resulting tracked-job object (newly inserted or updated),
    // or null if the SM rejected the transition (illegal/stale/typo).
    //
    // Design: docs/ux/ux-architecture-260506.md §"Job lifecycle".
    // Dep: this._setMainJobStatus (injected) OR window.setMainJobStatus (fallback for
    // callers that use the module-level singleton without supplying deps). The fallback
    // preserves backward-compat for the singleton's production wire-up path where
    // main-job-status.js assigns window.setMainJobStatus after module load.
    trackJob(serverJob, opts) {
        if (!serverJob?.id)
            return null;
        opts = opts ?? {};
        const id = serverJob.id;
        const prev = this._trackedJobs[id];
        // Resolve the SM function: prefer injected dep, fall back to window for production.
        const sms = this._setMainJobStatus ??
            (typeof window !== 'undefined' ? window : {}).setMainJobStatus;
        // Build the merged shape so non-status fields land regardless of
        // SM verdict.  Whole-row replacement ONLY when there's no prior;
        // otherwise merge (server payloads sometimes omit static fields).
        const merged = prev
            ? Object.assign({}, prev, serverJob)
            : Object.assign({}, serverJob);
        // Initial-write path: no prior tracked entry — accept the seed.
        if (!prev) {
            if (!sms)
                return null;
            const ok = sms(merged, serverJob.status, { ...(opts.eventTs !== undefined ? { eventTs: opts.eventTs } : {}) });
            if (!ok)
                return null;
            this._trackedJobs[id] = merged;
            return merged;
        }
        // Established job: route through the SM. We mutate `prev` in place
        // (status + extras) so callers retain reference identity. The SM
        // checks the legal table and stale-event watermark.
        const extra = Object.assign({}, serverJob);
        delete extra['status']; // status flows via the SM, not via Object.assign
        delete extra['id']; // id is the dict key, never mutated
        if (!sms)
            return null;
        const ok = sms(prev, serverJob.status, {
            ...(opts.eventTs !== undefined ? { eventTs: opts.eventTs } : {}),
            extra,
        });
        if (!ok)
            return null;
        return prev;
    }
    // ─── Pending-retry accessors ─────────────────────────────────────────────
    /** Register a pending retry; key is the caller-supplied synthetic id. */
    setPendingRetry(pendingId, payload) {
        this._pendingRetries[pendingId] = payload;
    }
    /** Look up a pending retry by id. */
    getPendingRetry(pendingId) {
        return this._pendingRetries[pendingId];
    }
    /** Drop a pending retry by id (typically after success/failure). */
    removePendingRetry(pendingId) {
        delete this._pendingRetries[pendingId];
    }
    /** All pending retries as `[id, payload]` tuples (used by render). */
    listPendingRetries() {
        return Object.entries(this._pendingRetries);
    }
    /** Count of pending retries (used by cancel-all + activity checks). */
    pendingRetryCount() {
        return Object.keys(this._pendingRetries).length;
    }
    // ─── Report-dialog accessors ─────────────────────────────────────────────
    /** Whether a Report-a-problem draft is open for this job. */
    hasReportDialog(jobId) {
        return Object.prototype.hasOwnProperty.call(this._openReportDialogs, jobId);
    }
    /** Get the Report-a-problem draft for this job (undefined if not open). */
    getReportDialog(jobId) {
        return this._openReportDialogs[jobId];
    }
    /** Set / replace the Report-a-problem draft for this job. */
    setReportDialog(jobId, draft) {
        this._openReportDialogs[jobId] = draft;
    }
    /** Drop the Report-a-problem draft for this job (submit / dismiss). */
    removeReportDialog(jobId) {
        delete this._openReportDialogs[jobId];
    }
    // ─── Cleared-id accessors ────────────────────────────────────────────────
    /** Mark a job id as cleared this session (suppresses re-injection). */
    addClearedId(id) {
        this._clearedJobIds.add(id);
    }
    /** Whether a job id has been cleared this session. */
    isCleared(id) {
        return this._clearedJobIds.has(id);
    }
    /** Persist cleared ids to sessionStorage (no-op without DOM). */
    saveClearedIds() {
        if (typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem('clearedJobIds', JSON.stringify([...this._clearedJobIds])); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        }
    }
    // ─── Persistence ─────────────────────────────────────────────────────────
    /** Persist tracked-job ids to localStorage (best-effort; Safari
     *  private-mode and full-storage are swallowed). */
    saveJobIds() {
        try {
            const ids = Object.keys(this._trackedJobs);
            localStorage.setItem('ada_job_ids', JSON.stringify(ids)); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        }
        catch (_e) {
            // Safari private mode or storage full — best-effort persistence.
        }
    }
    // ─── Monotonic-progress cache ────────────────────────────────────────────
    /** Clear all three monotonic-progress caches for a job (terminal-state
     *  cleanup). */
    clearMonotonicCache(jobId) {
        this._jobMonotonicPct.delete(jobId);
        this._jobMonotonicLabel.delete(jobId);
        this._jobMonotonicLabelPct.delete(jobId);
    }
    /** Read the current monotonic-percent for a job (undefined if absent). */
    getMonotonicPct(jobId) {
        return this._jobMonotonicPct.get(jobId);
    }
    /** Read the current monotonic-stage-label for a job (undefined if absent). */
    getMonotonicLabel(jobId) {
        return this._jobMonotonicLabel.get(jobId);
    }
    /** Read the percent paired with the current monotonic-stage-label (0 if absent). */
    getMonotonicLabelPct(jobId) {
        return this._jobMonotonicLabelPct.get(jobId) ?? 0;
    }
    /** Write the monotonic-percent for a job. */
    setMonotonicPct(jobId, v) {
        this._jobMonotonicPct.set(jobId, v);
    }
    /** Write the monotonic-stage-label for a job. */
    setMonotonicLabel(jobId, v) {
        this._jobMonotonicLabel.set(jobId, v);
    }
    /** Write the percent paired with the current monotonic-stage-label. */
    setMonotonicLabelPct(jobId, v) {
        this._jobMonotonicLabelPct.set(jobId, v);
    }
}
// ─── Module-level singleton ──────────────────────────────────────────────────
// The SPA carries exactly one JobState instance per page session. The class
// itself is exported above for direct construction in tests; the singleton
// here is what production code consumes.
export const jobState = new JobState();
